/**
 * The required-forms gate status for the current employee, mirroring the monolith's
 * `FormGateStatusResult` (API.Core/Interfaces/Authentication/IFormGateService.cs) returned by
 * `GET /auth/forms-gate/status`. Drives the forms-only completion screen and the review-due banner.
 */

/** Mirrors the backend `GateVerdict` enum (serialised as its numeric value). */
export enum GateVerdict {
  /** All required forms completed and current → full access. */
  Ok = 0,
  /** A completed submission is flagged for review (within its 14-day grace) → warn + time-box. */
  ReviewDue = 1,
  /** A required form is missing or a flag is overdue → restrict to form operations only. */
  Restricted = 2
}

/** A completed submission flagged for review, with its 14-day deadline. Mirrors the backend `FlaggedForm`. */
export interface FlaggedForm {
  formKey: string;
  flaggedAt: string;
  deadline: string;
  overdue: boolean;
  /**
   * True when the flag comes from the form's **Gültigkeitsdauer** having elapsed rather than from a reviewer
   * flagging the answer — the submission is stale and must be re-filled. Server-derived; never re-computed here.
   */
  expired: boolean;
  /**
   * The submission to re-confirm — the handle that makes a flagged form **self-serviceable**: the completion
   * screen starts the ordinary revise flow (`POST …/{id}/revise` → edit → finalise) on it, which supersedes the
   * stale answer and starts a fresh validity window. Server-picked (the most current answer); the SPA never
   * derives it from the submission list, or the two surfaces could disagree about which answer is the active one.
   * Absent when nothing revisable exists behind the flag — then the screen falls back to "Überprüfung ausstehend".
   */
  submissionId?: string | null;
}

/**
 * A required form that has no submission yet but is **not blocking**, because its requirement was assigned with
 * {@link BackfillPolicy.Grace}: the employee keeps full access until `deadline`. Mirrors the backend `PendingForm`.
 */
export interface PendingForm {
  formKey: string;
  /** When the grace ends — after this the verdict becomes `Restricted`. */
  deadline: string;
}

export interface FormsGateStatus {
  verdict: GateVerdict;
  /** Form keys that are required but have no submission yet — the forms to complete. */
  missingFormKeys: string[];
  flagged: FlaggedForm[];
  /**
   * Required forms still inside their backfill grace. Fillable **voluntarily** and listed on the completion
   * screen, but deliberately absent from {@link missingFormKeys} — they do not block the session.
   */
  pending: PendingForm[];
  earliestDeadline?: string | null;
  /** True when the Forms service was unreachable and the verdict is a fail-soft default. */
  unavailable: boolean;
}
